import moment, { Moment } from 'moment-timezone'
import { DateTime } from '@/stores/liteCMS/cmsData'
import { first, last, parseInt, sortBy } from 'lodash'
import { countUnit, pluralize } from '@/utils/stringUtils'
import { getI18nInstance } from '../i18n/setup'

/**
 * @deprecated: Replace with MonthNames[x].short from constants
 */
export const ABBREVIATED_MONTH_NAMES = {
  1: 'Jan',
  2: 'Feb',
  3: 'Mar',
  4: 'Apr',
  5: 'May',
  6: 'Jun',
  7: 'Jul',
  8: 'Aug',
  9: 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec',
}

export const getUtcTime = (date: any) => {
  const utcDate = moment.utc(date)
  if (utcDate.format('mm') === '00') {
    return utcDate.format('h A')
  }
  return utcDate.format('h:mm A')
}

export const getTimezoneTime = (date: any, timezone: any) => {
  const tzDate = moment.tz(date, timezone)
  if (tzDate.format('mm') === '00') {
    return tzDate.format('h A')
  }
  return tzDate.format('h:mm A')
}

const tc = (type: any, value: any) => {
  if (value <= 0) return ''

  switch (type) {
    case 'second':
      return value > 1 ? `${value} seconds` : `${value} second`
    case 'minute':
      return value > 1 ? `${value} minutes` : `${value} minute`
    case 'hour':
      return value > 1 ? `${value} hours` : `${value} hour`
    case 'day':
      return value > 1 ? `${value} days` : `${value} day`
  }
  return ''
}

export const formattedTimer = (seconds: any) => {
  const secondsInteger = Math.ceil(seconds)

  const minutes = Math.floor(secondsInteger / 60)
  const remainingSeconds = secondsInteger % 60

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24

  return [
    ['day', days],
    ['hour', remainingHours],
    ['minute', remainingMinutes],
    ['second', remainingSeconds],
  ]
    .map(pair => tc(pair[0], pair[1]))
    .filter(v => Boolean(v))
    .join(', ')
}

export const formatDate = (date: any, timezone = 'UTC', format = 'MMM D, YYYY') => {
  return moment.tz(date, timezone || 'UTC').format(format)
}

export const nullableDate = (str: any) => {
  if (typeof str !== 'string' || str.trim() === '') {
    return null
  }
  const date = new Date(str)
  return isNaN(date.getTime()) ? null : date
}

export function listDays(startDate: string | null, endDate: string | null) {
  const start = moment(startDate)
  const end = moment(endDate)
  const dates = [] as Moment[]
  while (start.isSameOrBefore(end, 'day')) {
    dates.push(start.clone())
    start.add(1, 'days')
  }
  return dates
}

export function datetime(dt: DateTime): Moment | null {
  if (dt.date) {
    const time = dt.time || '00:00:00'
    return moment(`${dt.date}T${time}Z`).tz('UTC')
  }
  return null
}

export function ymd(dt: any, tz: any = null): string | null {
  return toMoment(dt, tz)?.format('YYYY-MM-DD') ?? null
}

export function hms(dt: any, tz: any = null): string | null {
  return toMoment(dt, tz)?.format('HH:mm:ss') ?? null
}

function toMoment(dt: Moment | Date | string | null, tz: string | null = null): Moment | null {
  if (dt) {
    const date = moment(dt)
    if (tz) date.tz(tz)
    return date
  }
  return null
}

function sortDates(dates: (string | Date)[]): (string | Date)[] {
  return dates.sort((a, b) => {
    const [dtA, dtB] = [toMoment(a), toMoment(b)]
    return dtA && dtB ? dtA.valueOf() - dtB.valueOf() : 0
  })
}

export function earliest(...dates: (string | Date)[]): string | Date | undefined {
  return first(sortDates(dates).filter(d => moment(d).isValid()))
}

export function latest(...dates: (string | Date)[]): string | Date | undefined {
  return last(sortDates(dates.filter(d => moment(d).isValid())))
}

export function humanizeDuration(length: number, unit: any) {
  const duration = moment.duration(length, unit)
  const hours = Math.floor(duration.asHours())
  const minutes = duration.minutes()

  const parts = [] as string[]

  if (hours > 0) {
    parts.push(countUnit(hours, 'hr'))
  }

  if (minutes > 0 || hours === 0) {
    // Always include minutes if no hours
    parts.push(countUnit(minutes, 'min'))
  }

  return parts.join(' ')
}

export function formatMonthYear(months: Record<string | number, (string | number)[] | null>): string {
  const monthNames = Object.values(ABBREVIATED_MONTH_NAMES)

  // Extract and sort the years
  const years = Object.keys(months)
    .map(year => parseInt(year))
    .sort((a, b) => a - b)

  if (years.length === 0) return ''

  // Check if all month lists are empty or null - handle years-only case
  const hasAnyMonths = Object.values(months).some(
    monthList => monthList && Array.isArray(monthList) && monthList.length > 0,
  )

  if (!hasAnyMonths) {
    // Handle years-only formatting
    const yearRanges: Array<[number, number]> = [] // [startYear, endYear]
    let currentYearRange: [number, number] | null = null

    years.forEach(year => {
      if (!currentYearRange) {
        currentYearRange = [year, year]
        return
      }

      if (year === currentYearRange[1] + 1) {
        // Consecutive year
        currentYearRange[1] = year
      } else {
        // Non-consecutive year
        yearRanges.push([...currentYearRange])
        currentYearRange = [year, year]
      }
    })

    if (currentYearRange) {
      yearRanges.push(currentYearRange)
    }

    return yearRanges
      .map(([startYear, endYear]) => {
        if (startYear === endYear) {
          return `${startYear}`
        } else {
          return `${startYear} - ${endYear}`
        }
      })
      .join(' / ')
  }

  // Create and sort year-month pairs
  const yearMonthPairs = sortBy(
    Object.entries(months).flatMap(([year, monthList]) => {
      if (!monthList || !Array.isArray(monthList) || monthList.length === 0) {
        return []
      }
      return monthList.map(month => [parseInt(year), parseInt(month.toString())] as [number, number])
    }),
    [0, 1], // Sort by year, then by month
  )

  if (!yearMonthPairs.length) return ''

  // Group consecutive months
  const ranges: Array<[number, number, number, number]> = []
  let currentRange: [number, number, number, number] = [...yearMonthPairs[0], ...yearMonthPairs[0]]

  for (let i = 1; i < yearMonthPairs.length; i++) {
    const [year, month] = yearMonthPairs[i]
    const [, , endYear, endMonth] = currentRange

    const isConsecutive =
      (year === endYear && month === endMonth + 1) || // Same year, next month
      (year === endYear + 1 && month === 1 && endMonth === 12) // January of next year

    if (isConsecutive) {
      currentRange[2] = year
      currentRange[3] = month
    } else {
      ranges.push([...currentRange])
      currentRange = [year, month, year, month]
    }
  }

  ranges.push(currentRange)

  // Format each range
  return ranges
    .map(([startYear, startMonth, endYear, endMonth]) => {
      if (startYear === endYear && startMonth === endMonth) {
        return `${monthNames[startMonth - 1]} ${startYear}`
      } else if (startYear === endYear) {
        return `${monthNames[startMonth - 1]} - ${monthNames[endMonth - 1]} ${startYear}`
      } else {
        return `${monthNames[startMonth - 1]} ${startYear} - ${monthNames[endMonth - 1]} ${endYear}`
      }
    })
    .join(' / ')
}

export function formatTime(timeString: string) {
  // Check if the input is a valid string
  if (typeof timeString !== 'string' || !timeString.match(/^\d{2}:\d{2}:\d{2}$/)) {
    console.error("Invalid time string format. Please use 'HH:MM:SS'.")
    return null
  }

  // Use moment.js to parse the input string and format it
  const momentObject = moment(timeString, 'HH:mm:ss')

  // Check if the moment object is valid
  if (!momentObject.isValid()) {
    console.error('Failed to parse the time string with moment.js.')
    return null
  }

  return momentObject.format('h:mm A')
}

/**
 *
 * @param count - The number of unit
 * @param unit - The unit of the count (day / week / month / year)
 * @returns The pluralized formatted string
 */
export function formatDateUnit(count: number | string, unit: string) {
  const i18n = getI18nInstance().global

  const numericCount = typeof count === 'number' ? count : Number(count)

  switch (true) {
    case unit.includes('day'): {
      return `${numericCount} ${i18n.t('date.day', { count: numericCount })}`
    }
    case unit.includes('week'):
      return `${numericCount} ${i18n.t('date.week', { count: numericCount })}`
    case unit.includes('month'):
      return `${numericCount} ${i18n.t('date.month', { count: numericCount })}`
    case unit.includes('year'):
      return `${numericCount} ${i18n.t('date.year', { count: numericCount })}`
    default:
      return `${numericCount} ${pluralize(numericCount, unit)}`
  }
}

export enum DateFormat {
  SHORT = 'MMM D, YYYY',
  SHORT_TIME = 'hh:mm:ss A - MMM D, YYYY',
  LONG = 'MMMM D, YYYY',
  FULL = 'dddd, MMMM D, YYYY',
}
